// ContactPage.jsx
import React, { useMemo, useRef, useEffect } from "react";
import { Instances, Instance } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { Physics, RigidBody, BallCollider, CuboidCollider } from "@react-three/rapier";
import * as THREE from "three";

function BoundsBox({ halfExtents = [10, 6, 10] }) {
  const [hx, hy, hz] = halfExtents;

  return (
    <RigidBody type="fixed" colliders={false}>
      {/* floor & ceiling */}
      <CuboidCollider args={[hx, 0.1, hz]} position={[0, -hy, 0]} restitution={1} friction={0.1} />
      <CuboidCollider args={[hx, 0.1, hz]} position={[0,  hy, 0]} restitution={1} friction={0.1} />
      {/* left & right */}
      <CuboidCollider args={[0.1, hy, hz]} position={[-hx, 0, 0]} restitution={1} friction={0.1} />
      <CuboidCollider args={[0.1, hy, hz]} position={[ hx, 0, 0]} restitution={1} friction={0.1} />
      {/* front & back */}
      <CuboidCollider args={[hx, hy, 0.1]} position={[0, 0, -hz]} restitution={1} friction={0.1} />
      <CuboidCollider args={[hx, hy, 0.1]} position={[0, 0,  hz]} restitution={1} friction={0.1} />
    </RigidBody>
  );
}

// Simple seeded RNG so shapes are repeatable
function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Build a blobby cluster by growing neighbors from existing voxels
// Build a noisy spherical-ish cluster
// Build a mostly solid, slightly noisy voxel sphere
// Build a mostly solid, slightly noisy voxel sphere
function generateVoxelPositions(count = 60, seed = 1, jitterAmount = 1) {
  const safeCount = Math.max(1, count | 0);
  const rand = mulberry32(seed);

  // Approximate radius assuming a filled sphere:
  //   volume ≈ 4/3 π r^3 ≈ count
  const targetR = Math.cbrt((3 * safeCount) / (4 * Math.PI));

  // Search cube around origin
  const maxR = Math.ceil(targetR * 1.3);
  const candidates = [];

  // Controls how lumpy / imperfect it is
  const noiseAmount = 2;          // how much we perturb distance
  const holeChanceOuter = 0.08;   // chance to delete voxel near surface
  const holeChanceInner = 0.02;   // chance to delete voxel deep inside

  for (let x = -maxR; x <= maxR; x++) {
    for (let y = -maxR; y <= maxR; y++) {
      for (let z = -maxR; z <= maxR; z++) {
        const dist = Math.sqrt(x * x + y * y + z * z);

        // A little per-voxel noise so boundary isn't perfect
        const jitterDist = (rand() - 0.5) * noiseAmount;
        const d = dist + jitterDist;

        // Inside rough sphere?
        if (d <= targetR) {
          // Small chance of removing some voxels to keep it asteroid-y
          const norm = dist / targetR; // 0 = center, 1 ≈ surface
          const holeChance =
            norm > 0.7 ? holeChanceOuter : holeChanceInner; // more craters near surface

          if (rand() > holeChance) {
            // 👇 position jitter so they aren't perfectly on the grid
            const jx = (rand() - 0.5) * jitterAmount;
            const jy = (rand() - 0.5) * jitterAmount;
            const jz = (rand() - 0.5) * jitterAmount;

            // If you only want left/right (and forward/back), use jy = 0:
            // const jy = 0;

            candidates.push([x + jx, y + jy, z + jz]);
          }
        }
      }
    }
  }

  // If we ended up with fewer than requested, just return them all
  if (candidates.length <= safeCount) {
    return candidates;
  }

  // Seeded shuffle + slice to get exactly safeCount voxels
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = candidates[i];
    candidates[i] = candidates[j];
    candidates[j] = tmp;
  }

  return candidates.slice(0, safeCount);
}

function Meteor({
  center = [0, 0, 0],
  voxelSize = 0.6,
  voxelCount = 60,
  color = "#c2bee4ff",
  roughness = 1,
  metalness = 0,
  seed = 1,
}) {
  const bodyRef = useRef();

  // voxel positions for visual mesh
  const positions = useMemo(
    () => generateVoxelPositions(voxelCount, seed),
    [voxelCount, seed]
  );

  // per-voxel scale variance
  const scales = useMemo(
    () =>
      positions.map(() => {
        const s = 0.9 + Math.random() * 0.4;
        return [s, s, s];
      }),
    [positions.length]
  );

  // approximate spherical radius in world units for collider
  const approxRadius = useMemo(() => {
    const targetR = Math.cbrt((3 * voxelCount) / (4 * Math.PI)); // same idea as generator
    return targetR * voxelSize * 1.1; // little padding
  }, [voxelCount, voxelSize]);

  // random initial offset around center
  const initialOffset = useMemo(() => {
    const rand = mulberry32(seed + 111);
    const r = approxRadius * 0.8;
    return [
      (rand() * 2 - 1) * r,
      (rand() * 2 - 1) * r,
      (rand() * 2 - 1) * r,
    ];
  }, [seed, approxRadius]);

  // random linear velocity
  const initialLinvel = useMemo(() => {
    const rand = mulberry32(seed + 222);
    const speed = 2; // tweak for faster/slower motion
    return [
      (rand() * 2 - 1) * speed,
      (rand() * 2 - 1) * speed,
      (rand() * 2 - 1) * speed,
    ];
  }, [seed]);

  // random angular velocity
  const initialAngvel = useMemo(() => {
    const rand = mulberry32(seed + 333);
    const spin = 1.5; // radians/sec
    return [
      (rand() * 2 - 1) * spin,
      (rand() * 2 - 1) * spin,
      (rand() * 2 - 1) * spin,
    ];
  }, [seed]);

  // store last meaningful direction so we can nudge along it
  const lastDirRef = useRef(new THREE.Vector3(1, 0, 0));

  // kick the body once on mount
  useEffect(() => {
    if (!bodyRef.current) return;
    const [vx, vy, vz] = initialLinvel;
    const [ax, ay, az] = initialAngvel;

    bodyRef.current.setLinvel({ x: vx, y: vy, z: vz }, true);
    bodyRef.current.setAngvel({ x: ax, y: ay, z: az }, true);

    const dir = new THREE.Vector3(vx, vy, vz);
    if (dir.lengthSq() > 1e-6) {
      dir.normalize();
      lastDirRef.current.copy(dir);
    }
  }, [initialLinvel, initialAngvel]);

  const [cx, cy, cz] = center;
  const [ox, oy, oz] = initialOffset;

  // 🔁 keep the meteor moving if it's about to stop
  useFrame(() => {
    const body = bodyRef.current;
    if (!body) return;

    const v = body.linvel();
    const speedSq = v.x * v.x + v.y * v.y + v.z * v.z;

    const MIN_SPEED = 0.2;      // below this, we consider it "stalled"
    const MIN_SPEED_SQ = MIN_SPEED * MIN_SPEED;
    const NUDGE_STRENGTH = 0.6; // magnitude of the kick

    if (speedSq < MIN_SPEED_SQ) {
      const dir = new THREE.Vector3(v.x, v.y, v.z);

      if (dir.lengthSq() > 1e-8) {
        // use current direction if it's nonzero
        dir.normalize();
        lastDirRef.current.copy(dir);
      } else {
        // completely stopped: fall back to last known direction
        dir.copy(lastDirRef.current);
      }

      // gently push along that direction
      body.applyImpulse(
        {
          x: dir.x * NUDGE_STRENGTH,
          y: dir.y * NUDGE_STRENGTH,
          z: dir.z * NUDGE_STRENGTH,
        },
        true
      );
    } else {
      // while it's moving, keep lastDir in sync
      const dir = new THREE.Vector3(v.x, v.y, v.z).normalize();
      lastDirRef.current.copy(dir);
    }
  });

  return (
    <RigidBody
      ref={bodyRef}
      colliders={false}            // we'll define our own collider
      position={[cx + ox, cy + oy, cz + oz]}
      mass={1}
      restitution={1}             // bouncy
      friction={0.1}
      linearDamping={0.0}         // no drag (pure space)
      angularDamping={0.0}
    >
      {/* simple spherical collider for perf */}
      <BallCollider args={[approxRadius]} />

      <group>
        <Instances limit={positions.length}>
          <boxGeometry args={[voxelSize, voxelSize, voxelSize]} />
          <meshStandardMaterial
            color={color}
            roughness={roughness}
            metalness={metalness}
          />
          {positions.map((p, i) => (
            <Instance
              key={i}
              position={[
                p[0] * voxelSize,
                p[1] * voxelSize,
                p[2] * voxelSize,
              ]}
              scale={scales[i]}
            />
          ))}
        </Instances>
      </group>
    </RigidBody>
  );
}

const tiltA = [
  THREE.MathUtils.degToRad(20),
  0,
  THREE.MathUtils.degToRad(10),
];
const tiltB = [
  THREE.MathUtils.degToRad(-20),
  0,
  THREE.MathUtils.degToRad(0),
];

export default function ContactPage({
  orbitCenter = [4, -1, 0],   // now just spawn centers
  orbitCenter2 = [2, -1, 4],
}) {
  return (
    <Physics gravity={[0, 0, 0]} timeStep="vary">
      {/* bounding box the meteors stay inside */}
      <BoundsBox halfExtents={[10, 10, 15]} />

      {/* lights */}
      <ambientLight intensity={0.5} color={'#64e5ffff'} />
        <directionalLight
          position={[5, 10, 6]}
          intensity={6}
          color="#f2feff"
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-near={1}
          shadow-camera-far={40}
          shadow-camera-left={-10}
          shadow-camera-right={10}
          shadow-camera-top={10}
          shadow-camera-bottom={-10}
          shadow-bias={-0.0005}
        />

      {/* first cluster */}
      <Meteor
        center={orbitCenter}
        voxelCount={20}
        voxelSize={0.7}
        seed={1}
      />
      <Meteor
        center={orbitCenter}
        voxelCount={26}
        voxelSize={0.5}
        seed={2}
      />
      <Meteor
        center={orbitCenter}
        voxelCount={30}
        voxelSize={0.6}
        seed={3}
      />

      {/* second cluster */}
      <Meteor
        center={orbitCenter2}
        voxelCount={20}
        voxelSize={0.7}
        seed={4}
      />
      <Meteor
        center={orbitCenter2}
        voxelCount={26}
        voxelSize={0.5}
        seed={5}
      />
      <Meteor
        center={orbitCenter2}
        voxelCount={30}
        voxelSize={0.6}
        seed={6}
      />
      <Meteor
        center={orbitCenter2}
        voxelCount={24}
        voxelSize={0.6}
        seed={7}
      />
      <Meteor
        center={orbitCenter2}
        voxelCount={16}
        voxelSize={0.7}
        seed={8}
      />
      <Meteor
        center={orbitCenter2}
        voxelCount={28}
        voxelSize={0.6}
        seed={9}
      />
      <Meteor
        center={orbitCenter2}
        voxelCount={34}
        voxelSize={0.4}
        seed={10}
      />
    </Physics>
  );
}